import React, { useEffect, useState } from "react";
import { onUserEvent, coalesce } from "../lib/userEvents";
import { useNavigate } from "react-router-dom";
import { ClipboardList, ClipboardCheck, ExternalLink, Bot, Plus, Check, X, Loader2, ShieldCheck, Image as ImageIcon, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BatchToggle } from "./BatchOption";
import { confirmAsync } from "./ConfirmDialog";

// Workspace task pinned at the top of a task-linked chat so the discussion stays on topic.
export function TaskContextCard({ taskId, refreshKey, cid, onDetach }) {
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  useEffect(() => { if (taskId) api.get(`/tasks/${taskId}`).then((r) => setTask(r.data)).catch(() => setTask(null)); }, [taskId, refreshKey]);
  if (!task) return null;
  const status = { completed: ["#10B981", "Selesai"], running: ["#8B5CF6", "Berjalan"], queued: ["#F59E0B", "Antre"], failed: ["#EF4444", "Gagal"], scheduled: ["#2F6BFF", "Terjadwal"] }[task.status] || ["#94A3B8", task.status];
  return (
    <div className="mx-auto mb-3 w-full max-w-3xl rounded-2xl border border-[#2F6BFF]/25 bg-[#EEF3FF] p-3.5" data-testid="task-context-card">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2F6BFF] text-white"><ClipboardList size={17} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#2F6BFF]">Tugas Ruang Kerja · v{task.version}</p>
          <p className="truncate text-sm font-semibold text-slate-900" data-testid="task-context-title">{task.goal}</p>
          <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{(task.revision_note || task.summary || (task.final_output || "").slice(0, 160)).replace(/\*\*/g, "")}</p>
        </div>
        <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: `${status[0]}22`, color: status[0] }}>{status[1]}</span>
        {cid && onDetach && <button onClick={async () => { try { await api.delete(`/conversations/${cid}/task`); onDetach(); } catch (e) {} }} title="Lepas tugas dari chat ini" className="shrink-0 text-slate-400 hover:text-[#EF4444]" data-testid="task-context-detach"><X size={15} /></button>}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-slate-500">Minta perubahan di chat ini → revisi tersimpan otomatis sebagai versi baru.</span>
        <button onClick={() => nav(`/workspace/${task.id}`)} className="flex items-center gap-1 font-semibold text-[#2F6BFF]" data-testid="task-context-open"><ExternalLink size={12} /> Buka di Ruang Kerja</button>
      </div>
    </div>
  );
}

// "+ Asisten": invite another persona into the current conversation.
export function AddPersonaMenu({ conv, personas, onAdded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const inConv = new Set((conv.persona_ids || []).concat((conv.members || []).map((m) => m.id)));
  const options = (personas || []).filter((p) => !inConv.has(p.id) && !p.builtin);
  const add = async (p) => {
    setBusy(p.id);
    try { const r = await api.post(`/conversations/${conv.id}/personas`, { persona_id: p.id }); toast.success(`${p.name} bergabung`); setOpen(false); onAdded(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah asisten"); } finally { setBusy(""); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} title="Undang asisten lain ke percakapan ini" data-testid="add-persona-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-[#E6EAF2] bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 sm:px-3"><Bot size={15} /><Plus size={12} /> <span className="hidden sm:inline">Asisten</span></button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-64 rounded-2xl border border-[#E7ECF3] bg-white p-2 shadow-xl" data-testid="add-persona-menu">
          <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Undang asisten</p>
          {options.length === 0 && <p className="px-2 py-2 text-xs text-slate-500">Semua asisten sudah ada di percakapan ini.</p>}
          {options.map((p) => (
            <button key={p.id} onClick={() => add(p)} disabled={!!busy} data-testid={`add-persona-${p.id}`} className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-60">
              {p.portrait ? <img src={p.portrait} alt="" className="h-7 w-7 rounded-full object-cover" /> : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2F6BFF] text-xs font-bold text-white">{(p.name || "A")[0]}</span>}
              <span className="min-w-0 flex-1 truncate font-semibold text-slate-800">{p.name}</span>
              {busy === p.id ? <span className="text-xs text-slate-400">...</span> : <Check size={14} className="text-slate-300" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Quick replies under the assistant's offer: "bahas satu per satu" vs "terima beres".
export function TaskOfferButtons({ m, cid, onDone, isLast }) {
  const [busy, setBusy] = useState("");
  const [batch, setBatch] = useState(false);
  if (m.tool !== "task_offer" || !isLast) return null;
  if (m.pending_task?.workflow) return <WorkflowOfferCard m={m} cid={cid} onDone={onDone} />;
  const pick = async (mode) => {
    setBusy(mode);
    try { await api.post(`/conversations/${cid}/tasks/accept`, { mode, batch: batch && mode !== "discuss" }); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(""); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="task-offer-buttons">
      {m.pending_task?.batch_available && <BatchToggle on={batch} onChange={setBatch} disabled={!!busy} testId="task-offer-batch" />}
      <button onClick={() => pick("discuss")} disabled={!!busy} className="rounded-full border border-[#2F6BFF]/40 bg-white px-4 py-1.5 text-xs font-bold text-[#2F6BFF] hover:bg-[#EEF3FF] disabled:opacity-60" data-testid="task-offer-discuss">{busy === "discuss" ? "..." : "Bahas satu per satu"}</button>
      <button onClick={() => pick("delegate")} disabled={!!busy} className="rounded-full bg-[#2F6BFF] px-4 py-1.5 text-xs font-bold text-white hover:brightness-105 disabled:opacity-60" data-testid="task-offer-delegate">{busy === "delegate" ? "..." : "Terima beres"}</button>
    </div>
  );
}

// Workflow offer card: the drafted steps appear right in the chat — assistant per step (model follows the assistant), one image/video model for
// every step that needs media, draft vs run-now + batch. Mirrors the core per-step options of the Workspace editor.
export function WorkflowOfferCard({ m, cid, onDone }) {
  const [busy, setBusy] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [steps, setSteps] = useState([]);
  const [imageModel, setImageModel] = useState("");
  const [videoModel, setVideoModel] = useState("");
  const [batch, setBatch] = useState(false);
  useEffect(() => {
    let alive = true;
    api.post(`/conversations/${cid}/workflow/preview`).then((r) => {
      if (!alive) return;
      setData(r.data); setSteps(r.data.steps || []);
      setImageModel(r.data.steps?.find((s) => s.image_model)?.image_model || (r.data.image_models || []).find((x) => x.available)?.id || "");
      setVideoModel(r.data.steps?.find((s) => s.video_model)?.video_model || r.data.video_models?.[0]?.id || "");
    }).catch((e) => alive && setErr(e?.response?.data?.detail || "Gagal menyusun langkah"));
    return () => { alive = false; };
  }, [cid, m.id]);
  const assistants = data?.assistants || [];
  const byId = Object.fromEntries(assistants.map((a) => [a.id, a]));
  const batchAvail = !!data?.batch_available;
  const batchOk = batchAvail && steps.every((s) => byId[s.persona_id]?.batch_ok !== false);
  const modelsList = data?.models || [];
  const labelOf = (id) => modelsList.find((x) => x.id === id)?.label || id;
  const setModel = (id, model) => setSteps((a) => a.map((s) => (s.id === id ? { ...s, model: model || null } : s)));
  const go = async (run) => {
    setBusy(run ? "run" : "draft");
    try {
      await api.post(`/conversations/${cid}/workflow/accept`, { run, batch: batch && batchOk, assignments: steps.map((s) => ({ id: s.id, persona_id: s.persona_id, model: s.model || "" })), image_model: data?.media_needed?.image ? imageModel : null, video_model: data?.media_needed?.video ? videoModel : null });
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyusun alur kerja"); } finally { setBusy(""); }
  };
  if (err) return <p className="mt-2 text-xs text-[#EF4444]" data-testid="workflow-offer-error">{err}</p>;
  if (!data) return <div className="mt-2 flex items-center gap-2 text-xs text-slate-500" data-testid="workflow-offer-loading"><Loader2 size={13} className="animate-spin" /> Menyusun langkah alur kerja...</div>;
  return (
    <div className="mt-2 w-full max-w-xl rounded-2xl border border-[#7C3AED]/25 bg-[#F6F3FF] p-3" data-testid="workflow-offer-card">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#7C3AED]">Langkah alur kerja · {steps.length}</p>
      <ol className="space-y-1.5" data-testid="workflow-offer-steps">
        {steps.map((s, i) => (
          <li key={s.id} className="flex items-start gap-2 rounded-xl border border-[#E0D8F7] bg-white p-2" data-testid={`workflow-offer-step-${i}`}>
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0B132B] text-[10px] font-bold text-white">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-slate-900" title={s.description}>{s.title}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <label className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-600" title="Bawaan: rute model otomatis asisten untuk jenis langkah ini">Model
                  <select value={s.model || ""} onChange={(e) => setModel(s.id, e.target.value)} disabled={!!busy} className="bg-transparent font-semibold text-slate-900 outline-none" data-testid={`workflow-offer-model-${i}`} data-no-i18n>
                    <option value="">Otomatis · {labelOf(s.auto_model) || "rute asisten"}</option>
                    {modelsList.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                  </select>
                </label>
                {s.needs_approval && <span className="flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700"><ShieldCheck size={11} /> Persetujuan Anda</span>}
                {s.media_kind && <span className="flex items-center gap-1 rounded-lg bg-[#EEF3FF] px-1.5 py-0.5 text-[11px] font-semibold text-[#2F6BFF]">{s.media_kind === "image" ? <><ImageIcon size={11} /> Gambar</> : <><Film size={11} /> Video</>}</span>}
              </div>
            </div>
          </li>
        ))}
      </ol>
      {(data.media_needed?.image || data.media_needed?.video) && (
        <div className="mt-2.5 grid gap-2 sm:grid-cols-2" data-testid="workflow-offer-media">
          {data.media_needed.image && (
            <label className="text-[11px] font-bold text-slate-600">Model gambar <span className="font-normal text-slate-400">(semua langkah bergambar)</span>
              <select value={imageModel} onChange={(e) => setImageModel(e.target.value)} disabled={!!busy} data-testid="workflow-offer-image-model" className="mt-1 w-full rounded-xl border border-[#E0D8F7] bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 focus:border-[#7C3AED] focus:outline-none">
                <option value="">Tanpa gambar</option>
                {(data.image_models || []).map((x) => <option key={x.id} value={x.id} disabled={!x.available}>{x.label}{x.credits_default ? ` · ~${x.credits_default} kredit/gambar` : ""}{x.available ? "" : " (tak tersedia)"}</option>)}
              </select>
            </label>
          )}
          {data.media_needed.video && (
            <label className="text-[11px] font-bold text-slate-600">Model video <span className="font-normal text-slate-400">(semua langkah bervideo)</span>
              <select value={videoModel} onChange={(e) => setVideoModel(e.target.value)} disabled={!!busy} data-testid="workflow-offer-video-model" className="mt-1 w-full rounded-xl border border-[#E0D8F7] bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 focus:border-[#7C3AED] focus:outline-none">
                <option value="">Tanpa video</option>
                {(data.video_models || []).map((x) => <option key={x.id} value={x.id}>{x.label}{x.credits ? ` · ~${x.credits} kredit/8 dtk` : ""}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      {batchAvail && (
        <label className={`mt-2.5 flex items-center gap-2 text-xs font-semibold ${batchOk ? "text-slate-700" : "text-slate-400"}`} data-testid="workflow-offer-batch">
          <input type="checkbox" checked={batch && batchOk} disabled={!batchOk || !!busy} onChange={(e) => setBatch(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#7C3AED]" />
          Santai saja — selesai paling lambat besok (batch, <b className="ml-0.5">50% lebih hemat</b>){batchOk ? "" : " · model salah satu asisten belum mendukung batch"}
        </label>
      )}
      <p className="mt-2 text-[11px] text-slate-500">Model mengikuti asisten yang dipilih. Opsi lanjutan (konteks, lampiran, ukuran media) bisa diatur di Ruang Kerja setelah draf dibuat.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => go(false)} disabled={!!busy} className="rounded-full border border-[#7C3AED]/40 bg-white px-4 py-1.5 text-xs font-bold text-[#7C3AED] hover:bg-[#EDE7FB] disabled:opacity-60" data-testid="workflow-offer-draft">{busy === "draft" ? "..." : "Buat draf dulu"}</button>
        <button onClick={() => go(true)} disabled={!!busy} className="rounded-full bg-[#7C3AED] px-4 py-1.5 text-xs font-bold text-white hover:brightness-105 disabled:opacity-60" data-testid="workflow-offer-run">{busy === "run" ? "..." : "Jalankan sekarang"}</button>
      </div>
    </div>
  );
}

// Polls for finished assigned tasks and shows a toast with a link to the result.
export function TaskNotifier() {
  const nav = useNavigate();
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get("/task-notifications");
        if (stop || !r.data.length) return;
        r.data.forEach((t) => toast(t.status === "completed" ? `Tugas selesai: ${t.goal}` : `Tugas gagal: ${t.goal}`, { description: t.persona_name ? `oleh ${t.persona_name}` : undefined, action: { label: "Buka", onClick: () => nav(`/workspace/${t.id}`) }, duration: 12000 }));
        window.dispatchEvent(new CustomEvent("oryntix:task-done", { detail: r.data }));
        await api.post("/task-notifications/ack");
      } catch (e) {}
    };
    poll(); const off = onUserEvent(["task_update", "ws_state"], () => coalesce("task-notifs", poll)); // trigger → GET
    return () => { stop = true; off(); };
  }, [nav]);
  return null;
}


// Clickable Workspace search results under the assistant's reply.
export function WorkspaceResults({ m }) {
  const nav = useNavigate();
  if (m.tool !== "workspace_search" || !(m.results || []).length) return null;
  return (
    <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="workspace-results">
      {m.results.map((r) => (
        <button key={r.id} onClick={() => nav(`/workspace/${r.id}`)} data-testid={`workspace-result-${r.id}`} className="flex items-start gap-2 rounded-xl border border-[#E7ECF3] bg-white p-2.5 text-left hover:border-[#2F6BFF]/50 hover:bg-[#EEF3FF]/40">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#2F6BFF] text-white"><ClipboardList size={14} /></span>
          <span className="min-w-0"><span className="block truncate text-xs font-bold text-slate-900">{r.title}</span><span className="block text-[10px] text-slate-500">v{r.version}{r.persona_name ? ` · ${r.persona_name}` : ""} · {new Date(r.updated_at).toLocaleDateString("id-ID")}</span></span>
        </button>
      ))}
    </div>
  );
}

// Archive search results / restore confirmation posted by the assistant (tool: archive_search | archive_confirm).
export function ArchiveResults({ m, cid, onDone, isLast }) {
  const [busy, setBusy] = useState("");
  if (!["archive_search", "archive_confirm"].includes(m.tool) || !(m.results || []).length) return null;
  const restore = async (a) => {
    if (!await confirmAsync(`Pulihkan arsip «${a.title}» ke chat ini?`)) return;
    setBusy(a.id);
    try { await api.post(`/archives/${a.id}/restore`); toast.success("Arsip dipulihkan"); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memulihkan"); } finally { setBusy(""); }
  };
  const answer = async (text) => {
    setBusy(text);
    try { await api.post(`/conversations/${cid}/send`, { content: text }); onDone && onDone(); }
    catch (e) { toast.error("Gagal mengirim"); } finally { setBusy(""); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="archive-results">
      {m.results.slice(0, 5).map((a) => (
        <div key={a.id} className="flex items-center gap-3 rounded-xl border border-[#E7ECF3] bg-white p-3" data-testid={`archive-result-${a.id}`}>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0B132B] text-white"><ClipboardList size={14} /></span>
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-900">{a.title}</span><span className="block truncate text-[11px] text-slate-400">{a.message_count} pesan · {(a.persona_names || []).join(", ")}</span></span>
          {!a.restored && m.tool === "archive_search" && <button onClick={() => restore(a)} disabled={!!busy} className="rounded-lg bg-[#EEF3FF] px-2.5 py-1.5 text-xs font-bold text-[#2F6BFF]" data-testid="archive-result-restore">{busy === a.id ? "..." : "Pulihkan"}</button>}
        </div>
      ))}
      {m.tool === "archive_confirm" && isLast && (
        <div className="flex gap-2" data-testid="archive-confirm-buttons">
          <button onClick={() => answer("ya, pulihkan")} disabled={!!busy} className="rounded-full bg-[#10B981] px-4 py-1.5 text-xs font-bold text-white" data-testid="archive-confirm-yes">Ya, pulihkan</button>
          <button onClick={() => answer("tidak")} disabled={!!busy} className="rounded-full border border-[#E7ECF3] bg-white px-4 py-1.5 text-xs font-semibold text-slate-700" data-testid="archive-confirm-no">Tidak</button>
        </div>
      )}
    </div>
  );
}

// "Terapkan ke dokumen": save this assistant reply as the next version of the linked workspace document (no LLM involved).
export function ApplyToDocButton({ taskId, message, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const apply = async (mode) => {
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${taskId}/apply-message`, { message_id: message.id, mode });
      toast.success(`Dokumen diperbarui → v${r.data.version}`); setOpen(false); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerapkan ke dokumen"); } finally { setBusy(false); }
  };
  return (
    <span className="relative">
      <button onClick={() => setOpen((o) => !o)} title="Terapkan isi balasan ini ke dokumen di Ruang Kerja" aria-label="Terapkan ke dokumen" data-testid="msg-apply-doc-btn" className="flex items-center gap-1 text-[11px] font-semibold text-[#2F6BFF] hover:underline"><ClipboardCheck size={14} /> Terapkan ke dokumen</button>
      {open && (
        <span className="absolute bottom-6 left-0 z-20 flex w-64 flex-col gap-1 rounded-xl border border-[#E7ECF3] bg-white p-2 text-xs shadow-xl" data-testid="apply-doc-menu">
          <button disabled={busy} onClick={() => apply("replace")} data-testid="apply-doc-replace" className="rounded-lg px-3 py-2 text-left hover:bg-[#EEF3FF]"><span className="font-semibold">Ganti seluruh isi dokumen</span><span className="block text-slate-500">Balasan ini menjadi versi baru dokumen</span></button>
          <button disabled={busy} onClick={() => apply("append")} data-testid="apply-doc-append" className="rounded-lg px-3 py-2 text-left hover:bg-[#EEF3FF]"><span className="font-semibold">Tambahkan di akhir dokumen</span><span className="block text-slate-500">Isi lama dipertahankan</span></button>
        </span>
      )}
    </span>
  );
}

// Model-switch confirmation: the assistant never switches models silently; the user picks (default = the assistant's own model).
export function ModelChoiceCard({ m, onPick }) {
  const [busy, setBusy] = useState(false);
  const opts = m.choice?.options || [];
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="model-choice-card">
      {opts.map((o) => (
        <button key={o.id} disabled={busy} onClick={() => { setBusy(true); onPick(o.id); }} data-testid={`model-choice-${o.id}`}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${o.default ? "border-[#2F6BFF] bg-[#2F6BFF] text-white hover:brightness-110" : "border-[#E7ECF3] bg-white text-slate-700 hover:bg-[#EEF3FF]"}`}>
          {o.default ? <Check size={12} className="mr-1 inline" /> : <Bot size={12} className="mr-1 inline" />}{o.label}{o.default ? " (model asisten)" : ""}
        </button>
      ))}
    </div>
  );
}


// Ringkasan Kebutuhan (Luna orchestrator): confirm → the specialist model works with this brief; corrections can simply be typed in chat.
export function BriefCard({ m, cid, onConfirm, isLast }) {
  const [busy, setBusy] = useState(false);
  const [b, setB] = useState(m.brief || {});  // the rows themselves are in the message text (works on every client) — the card carries title, model, cost and the action
  const [opts, setOpts] = useState([]);
  useEffect(() => { setB(m.brief || {}); }, [m.brief]);
  useEffect(() => {
    if (!isLast) return;
    api.get("/me/model-routes").then((r) => { const kind = (r.data.categories || []).find((c) => c.id === b.category)?.kind || "text"; setOpts((r.data.options?.[kind] || []).filter((o) => o.id !== "auto")); }).catch(() => {});
    // eslint-disable-next-line
  }, [isLast, b.category]);
  const changeModel = async (id) => {
    try { const r = await api.put(`/conversations/${cid}/intake-model`, { model: id, message_id: m.id }); setB((x) => ({ ...x, model_key: r.data.model_key, model_label: r.data.model_label, est_credits: r.data.est_credits })); toast.success(`Model diganti ke ${r.data.model_label}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengganti model"); }
  };
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-[#D9E2F2] bg-[#F5F8FF]" data-testid="brief-card">
      <div className="flex items-center justify-between gap-2 border-b border-[#D9E2F2] px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#2F6BFF]">Ringkasan Kebutuhan</span>
        {b.title && <span className="truncate text-xs font-semibold text-slate-700" data-testid="brief-title">{b.title}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 bg-white/70 px-3 py-2">
        {isLast && opts.length && !b.media ? (
          <label className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="brief-model"><Bot size={11} />
            <select value={b.model_key || ""} onChange={(e) => changeModel(e.target.value)} className="bg-transparent font-semibold text-slate-800 outline-none" data-testid="brief-model-select" data-no-i18n>
              {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select> · {b.category_label}
          </label>
        ) : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="brief-model"><Bot size={11} /> {b.model_label} · {b.category_label}</span>}
        {b.est_credits > 0 && <span className="text-[10px] text-slate-500" data-testid="brief-est">≈ {b.est_credits} kredit</span>}
        {isLast && (
          <span className="ml-auto flex items-center gap-2">
            <span className="hidden text-[10px] text-slate-400 sm:inline">atau tulis perubahan di chat</span>
            <button disabled={busy} onClick={() => { setBusy(true); onConfirm(); }} data-testid="brief-confirm-btn"
              className="rounded-full bg-[#2F6BFF] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-60">
              {busy ? <Loader2 size={12} className="mr-1 inline animate-spin" /> : <Check size={12} className="mr-1 inline" />}Lanjutkan
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
